import { Button } from "@/components/ui/button";
import { ArrowRight, Play } from "lucide-react";
import { useLanguage } from "@/lib/i18n";

export default function HeroSection() {
  const { t } = useLanguage();

  return (
    <section className="relative min-h-screen flex items-center pt-36 pb-16 overflow-hidden">
      {/* Background with overlay */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/5" />

        {/* Animated lines decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-50" />
          <div className="absolute bottom-10 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
          <div className="absolute bottom-20 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
        </div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <p className="text-primary font-semibold tracking-wider uppercase">{t("hero.tagline")}</p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-balance">
                <span className="text-foreground">{t("hero.title1")}</span>{" "}
                <span className="text-primary">{t("hero.title2")}</span>{" "}
                <span className="text-foreground">{t("hero.title3")}</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">{t("hero.description")}</p>
            </div>

            {/* Dual CTAs for both audiences */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" asChild className="group">
                <a href="#services">
                  {t("hero.services")}
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                asChild
                className="group bg-transparent border-primary/50 hover:bg-primary/10"
              >
                <a href="#apply">
                  <Play className="mr-2 h-5 w-5" />
                  {t("hero.driveWithUs")}
                </a>
              </Button>
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-border">
              <div>
                <p className="text-3xl font-bold text-primary">10+</p>
                <p className="text-sm text-muted-foreground">{t("hero.yearsExp")}</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-primary">50+</p>
                <p className="text-sm text-muted-foreground">{t("hero.activeDrivers")}</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-primary">98%</p>
                <p className="text-sm text-muted-foreground">{t("hero.onTime")}</p>
              </div>
            </div>
          </div>

          {/* Truck image */}
          <div className="relative hidden lg:block">
            <div className="relative aspect-[4/3] rounded-xl overflow-hidden shadow-2xl border border-border">
              <img
                src="/images/image.png"
                alt="Reyes Trux Corp Freightliner Truck"
                className="object-cover w-full h-full"
              />
            </div>

            {/* Floating info cards with updated styling */}
            <div className="absolute top-4 right-4 bg-card/95 backdrop-blur-sm border border-primary/30 rounded-lg p-4 shadow-lg">
              <p className="text-xs text-muted-foreground">USDOT</p>
              <p className="text-lg font-bold text-primary">2864065</p>
            </div>

            <div className="absolute bottom-4 left-4 bg-card/95 backdrop-blur-sm border border-primary/30 rounded-lg p-4 shadow-lg">
              <p className="text-xs text-muted-foreground">MC#</p>
              <p className="text-lg font-bold text-primary">972857</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-muted-foreground rounded-full flex justify-center">
          <div className="w-1 h-3 bg-primary rounded-full mt-2 animate-pulse" />
        </div>
      </div>
    </section>
  );
}
